import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import Navbar from "./components/Navbar/Navbar";
import BottomTabBar from "./components/BottomTabBar/BottomTabBar";
import BackButton from "./components/BackButton/BackButton";
import Footer from "./components/Footer/Footer";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import CookieBanner from "./components/CookieBanner/CookieBanner";
import { ConfirmDialogProvider } from "./components/ConfirmDialog/ConfirmDialogProvider";

import AppRoutes from "./routes/AppRoutes";
import './App.css';

const decodeJwtPayload = (token) => {
  try {
    const payloadPart = token?.split(".")?.[1];
    if (!payloadPart) return null;
    const base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
};

const isTokenExpired = (token, skewSeconds = 30) => {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return true;
  return payload.exp * 1000 <= Date.now() + skewSeconds * 1000;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [userData, setUserData] = useState(null);
  const [profilePictureVersion, setProfilePictureVersion] = useState(0);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    const storedVersion = localStorage.getItem("profilePictureVersion");
    if (storedVersion) {
      const parsed = Number(storedVersion);
      if (!Number.isNaN(parsed)) setProfilePictureVersion(parsed);
    }
  }, []);

  const handleProfilePictureVersionUpdate = useCallback((version) => {
    if (!version) return;
    setProfilePictureVersion(version);
    localStorage.setItem("profilePictureVersion", String(version));
  }, []);

  // Logout clears tokens
  const handleLogout = useCallback(() => {
    localStorage.removeItem("idToken");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("profilePictureVersion");
    setIsAuthenticated(false);

    // Optional: if you also want to reset userData on logout
    setUserData(null);
    setProfilePictureVersion(0);
  }, []);

  // Login sets isAuthenticated and all tokens
  const handleLogin = useCallback((idToken, accessToken, refreshToken) => {
    if (idToken) localStorage.setItem("idToken", idToken);
    if (accessToken) localStorage.setItem("accessToken", accessToken);
    if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
    setIsAuthenticated(Boolean(idToken || localStorage.getItem("idToken")));
  }, []);

  const refreshTokens = useCallback(async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      handleLogout();
      return false;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/refresh-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error("Failed to refresh tokens");
      }

      const { idToken, accessToken } = await response.json();
      localStorage.setItem("idToken", idToken);
      localStorage.setItem("accessToken", accessToken);
      setIsAuthenticated(true);

      return true;
    } catch (error) {
      console.error("Error refreshing tokens:", error);
      handleLogout();
      return false;
    }
  }, [handleLogout]);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      const idToken = localStorage.getItem("idToken");

      if (!idToken) {
        if (mounted) {
          setIsAuthenticated(false);
          setIsAuthInitialized(true);
        }
        return;
      }

      if (!isTokenExpired(idToken)) {
        if (mounted) {
          setIsAuthenticated(true);
          setIsAuthInitialized(true);
        }
        return;
      }

      const refreshed = await refreshTokens();
      if (mounted) {
        setIsAuthenticated(refreshed);
        setIsAuthInitialized(true);
      }
    };

    initializeAuth();
    return () => {
      mounted = false;
    };
  }, [refreshTokens]);

  if (!isAuthInitialized) return null;

  return (
    <Router>
      <ConfirmDialogProvider>
        <ScrollToTop />
        <div className="App">
          {/* Pass setUserData as onUserFetch to Navbar so it can store the fetched user */}
          <Navbar
            isAuthenticated={isAuthenticated}
            handleLogout={handleLogout}
            refreshTokens={refreshTokens}
            onUserFetch={setUserData}
            userData={userData}
            profilePictureVersion={profilePictureVersion}
          />

          {/* Phone widths only, on every page but Home and Profile, as in the app. */}
          <BackButton />

          {/* Pass userData to AppRoutes so child routes (Home, Profile, etc.) can receive it */}
          <AppRoutes 
            isAuthenticated={isAuthenticated} 
            onLogin={handleLogin}
            refreshTokens={refreshTokens}
            handleLogout={handleLogout}
            userData={userData}
            onUserUpdate={setUserData}
            onProfilePictureVersionUpdate={handleProfilePictureVersionUpdate}
            profilePictureVersion={profilePictureVersion}
          />

          <Footer />
          {/* Phone widths only; it replaces the top navbar there. */}
          <BottomTabBar isAuthenticated={isAuthenticated} />
          <CookieBanner />

        </div>
      </ConfirmDialogProvider>
    </Router>
  );
}

export default App;
